import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import "./OnboardingWizard.scss";

const TOTAL_STEPS = 4;
const STORAGE_PREFIX = "cloudstore_console_onboarding_";

/**
 * 开屏引导（4 步）。用户登录后只弹一次，完成/跳过后按用户记录到 localStorage。
 * 渲染在 ConsoleLayout 内，覆盖整个控制台。
 */
const OnboardingWizard = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  const storageKey = user ? `${STORAGE_PREFIX}${user.userId}` : null;

  useEffect(() => {
    if (!storageKey) return;
    try {
      if (!localStorage.getItem(storageKey)) {
        setStep(0);
        setVisible(true);
      }
    } catch {
      /* localStorage 不可用时不弹 */
    }
  }, [storageKey]);

  const finish = () => {
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, "1");
      } catch {
        /* ignore */
      }
    }
    setVisible(false);
  };

  if (!visible) return null;

  const renderBody = () => {
    switch (step) {
      case 0:
        return (
          <div className="cs-onb__body">
            <div className="cs-onb__icon">🚀</div>
            <h2 className="cs-onb__title">Welcome to CloudStore AI</h2>
            <p className="cs-onb__desc">
              Your Free Plan is now active with $2.00 monthly credits.
            </p>
            <p className="cs-onb__desc">
              Let's get you set up so you can start making API calls.
            </p>
          </div>
        );
      case 1:
        return (
          <div className="cs-onb__body">
            <div className="cs-onb__icon">🔑</div>
            <h2 className="cs-onb__title">Create Your First API Key</h2>
            <p className="cs-onb__desc">
              Generate an API key to start making calls to CloudStore APIs.
            </p>
          </div>
        );
      case 2:
        return (
          <div className="cs-onb__body">
            <div className="cs-onb__icon">🚀</div>
            <h2 className="cs-onb__title">Make Your First API Call</h2>
            <p className="cs-onb__desc">
              Try using our API. Check out the API Documentation for details.
            </p>
            <a
              className="cs-onb__link"
              href="/api-docs/index.html"
              target="_blank"
              rel="noreferrer"
            >
              API Documentation
            </a>
          </div>
        );
      default:
        return (
          <div className="cs-onb__body">
            <div className="cs-onb__icon">⭐</div>
            <h2 className="cs-onb__title">Choose Your Plan</h2>
            <p className="cs-onb__desc">
              Start free, upgrade when you're ready.
            </p>
            <div className="cs-onb__plans">
              <div className="cs-onb__plan">
                <div className="cs-onb__plan-name">Free</div>
                <div className="cs-onb__plan-price">
                  $0<span className="cs-onb__plan-per">/mo</span>
                </div>
                <div className="cs-onb__plan-credits">$2 credits/month</div>
              </div>
              <div className="cs-onb__plan cs-onb__plan--popular">
                <span className="cs-onb__plan-badge">POPULAR</span>
                <div className="cs-onb__plan-name">Developer</div>
                <div className="cs-onb__plan-price">
                  $99<span className="cs-onb__plan-per">/mo</span>
                </div>
                <div className="cs-onb__plan-credits">$50 credits/month</div>
              </div>
            </div>
          </div>
        );
    }
  };

  const isLast = step === TOTAL_STEPS - 1;

  return (
    <div className="cs-onb__overlay">
      <div className="cs-onb__card">
        {renderBody()}

        {/* 分页圆点 */}
        <div className="cs-onb__dots">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <span
              key={i}
              className={`cs-onb__dot${
                i === step ? " cs-onb__dot--active" : ""
              }`}
            />
          ))}
        </div>

        {/* 底部操作 */}
        <div className="cs-onb__footer">
          {step === 0 ? (
            <button type="button" className="cs-onb__text-btn" onClick={finish}>
              Skip all
            </button>
          ) : (
            <button
              type="button"
              className="cs-onb__text-btn"
              onClick={() => setStep((s) => s - 1)}
            >
              ← Previous
            </button>
          )}

          <button
            type="button"
            className="cs-onb__next"
            onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
          >
            {isLast ? "Get Started" : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
